import { effect, inject, Signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { differenceInCalendarDays } from 'date-fns';
import { map } from 'rxjs/operators';
import { UserPaymentSettings, UserPaymentStatusType } from '@shared/api/api-loop/models';
import { UserAuthService } from '@shared/modules/auth/auth-data-access/user-auth.service';
import { ToastPosition, ToastService } from '@shared/ui/kit/toast/toast.service';
import { WebOnboardingService } from '@shared/services/web-onboarding/web-onboarding.service';
import { distinctUntilChanged } from 'rxjs';

let initialized = false;

export function initTrialNotice(): void {
  if (initialized) {
    return;
  }
  initialized = true;
  const userAuthService = inject(UserAuthService);
  const toastService = inject(ToastService);
  const webOnboardingService = inject(WebOnboardingService);

  let activeNoticeId: string | null = null;

  const paymentSettings: Signal<UserPaymentSettings> = toSignal(
    userAuthService.currentSession$.pipe(
      map(s => s?.userSettings?.userPaymentSettings),
      distinctUntilChanged((a, b) => a?.paymentStatusType === b?.paymentStatusType)
    ),
    { initialValue: null }
  );

  effect(() => {
    const settings = paymentSettings();
    if (!settings) {
      return;
    }

    if (settings.paymentStatusType === UserPaymentStatusType.TRIAL) {
      const daysLeft = Math.max(0, differenceInCalendarDays(new Date(settings.paymentStatusDate), new Date()));
      const message =
        daysLeft === 0
          ? 'Your free trial will expire today.'
          : `You only have ${daysLeft} ${daysLeft === 1 ? 'day' : 'days'} left in your free trial.`;

      activeNoticeId = toastService.warning({
        title: message,
        link: {
          text: 'Subscribe now',
          actionFn: () => webOnboardingService.openWebSettings(new URLSearchParams(), '/billing', window.open())
        },
        isPermanent: true,
        position: ToastPosition.right
      });
    } else if (settings.paymentStatusType === UserPaymentStatusType.SUSPEND) {
      activeNoticeId = toastService.error({
        title: 'Your free trial has now expired. Please subscribe so you can continue using Loop.',
        link: {
          text: 'Subscribe now',
          actionFn: () => webOnboardingService.openWebSettings(new URLSearchParams(), '/billing', window.open())
        },
        isPermanent: true,
        position: ToastPosition.right
      });
    } else if (activeNoticeId) {
      toastService.dismiss(activeNoticeId);
      activeNoticeId = null;
    }
  });
}
